<template>
    <div v-if="can('baggage-shipping.create')" class="attach-baggage">
        <nav aria-label="breadcrumb">
            <ol class="breadcrumb">
                <router-link to="/baggage-shipping/create" tag="li" class="breadcrumb-item">{{i18n('Attach baggage')}}</router-link>
            </ol>
        </nav>

        <div class="base_box_hdr">
            <div class="base_box_ttl">
                <h1 class="title">{{i18n('Attach baggage')}}</h1>
            </div>
        </div>

        <div class="base_box_main">

            <!-- Step 1: search -->
            <div class="base_box ab-card">
                <div class="base_box_cnt">
                    <div class="ab-card-label"><i class="mdi mdi-magnify"></i> {{i18n('Passenger ID')}}</div>
                    <div class="ab-search-row">
                        <input type="text" class="form-control base_input" v-model="passenger_id"
                               :placeholder="i18n('Passenger ID')"
                               @keypress.enter="findPassenger"
                               :disabled="searching">
                        <button type="button" class="btn base_btn solid_blue ab-search-btn"
                                :disabled="!passenger_id || searching" @click="findPassenger">
                            <i class="mdi" :class="searching ? 'mdi-loading mdi-spin' : 'mdi-magnify'"></i>
                            {{i18n('Search')}}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Step 2: passenger summary -->
            <div v-if="passenger" class="base_box ab-card ab-fade-in">
                <div class="base_box_cnt">
                    <div class="ab-info-grid">
                        <div class="ab-info-item">
                            <i class="mdi mdi-account-outline ab-info-icon"></i>
                            <div>
                                <div class="ab-info-label">{{i18n('Passenger')}}</div>
                                <div class="ab-info-value">{{ passenger.full_name }}</div>
                            </div>
                        </div>
                        <div class="ab-info-item">
                            <i class="mdi mdi-seat-passenger ab-info-icon"></i>
                            <div>
                                <div class="ab-info-label">{{i18n('Bus seat')}}</div>
                                <div class="ab-info-value">{{ passenger.seat_number }}</div>
                            </div>
                        </div>
                        <div class="ab-info-item">
                            <i class="mdi mdi-map-marker-distance ab-info-icon"></i>
                            <div>
                                <div class="ab-info-label">{{i18n('Trip')}}</div>
                                <div class="ab-info-value">{{ passenger.route_name }}</div>
                            </div>
                        </div>
                        <div class="ab-info-item">
                            <i class="mdi mdi-clock-outline ab-info-icon"></i>
                            <div>
                                <div class="ab-info-label">{{i18n('Departure time')}}</div>
                                <div class="ab-info-value">{{ passenger.trip_time }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Existing baggage -->
            <div v-if="passenger && passenger.existing_baggages && passenger.existing_baggages.length > 0" class="base_box ab-card ab-fade-in">
                <div class="base_box_cnt">
                    <div class="ab-section-hdr">
                        <div class="ab-section-ttl">
                            <i class="mdi mdi-bag-checked"></i>
                            {{i18n('Previously attached baggage')}}
                            <span class="ab-badge">{{ passenger.existing_baggages.length }}</span>
                        </div>
                        <button type="button" class="btn base_btn border_white ab-print-all"
                                @click="printAll(passenger.existing_baggages)">
                            <i class="mdi mdi-printer"></i> {{i18n('Print all')}}
                        </button>
                    </div>
                    <div class="ab-baggage-list">
                        <div v-for="(baggage, index) in passenger.existing_baggages" :key="'existing-' + baggage.id" class="ab-baggage-row">
                            <div class="ab-baggage-row-left">
                                <i class="mdi mdi-bag-personal-outline ab-baggage-icon"></i>
                                <div>
                                    <div class="ab-info-value">#{{ index + 1 }} — ID {{ baggage.id }}</div>
                                    <div class="ab-info-label">{{ baggage.created_at }}</div>
                                </div>
                            </div>
                            <a :href="baggage.view_url" target="_blank" class="btn-icon" :title="i18n('Print out')">
                                <i class="mdi mdi-printer text-primary"></i>
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Step 3: attach -->
            <div v-if="passenger" class="base_box ab-card ab-fade-in">
                <div class="base_box_cnt">
                    <div class="ab-card-label"><i class="mdi mdi-bag-suitcase"></i> {{i18n('Baggage count')}}</div>
                    <div class="ab-attach-row">
                        <div class="ab-stepper">
                            <button type="button" class="ab-stepper-btn" :disabled="attaching || count <= 1" @click="count = Math.max(1, (count || 1) - 1)">
                                <i class="mdi mdi-minus"></i>
                            </button>
                            <input type="number" min="1" max="20" class="ab-stepper-input" v-model.number="count" :disabled="attaching">
                            <button type="button" class="ab-stepper-btn" :disabled="attaching || count >= 20" @click="count = Math.min(20, (count || 0) + 1)">
                                <i class="mdi mdi-plus"></i>
                            </button>
                        </div>
                        <button type="button" class="btn base_btn solid_blue ab-attach-btn"
                                :disabled="!count || attaching" @click="attachBaggage">
                            <i class="mdi" :class="attaching ? 'mdi-loading mdi-spin' : 'mdi-check'"></i>
                            {{i18n('Attach')}}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Newly attached -->
            <div v-if="createdBaggages.length > 0" class="base_box ab-card ab-fade-in ab-success-card">
                <div class="base_box_cnt">
                    <div class="ab-section-hdr">
                        <div class="ab-section-ttl">
                            <i class="mdi mdi-check-circle text-success"></i>
                            {{i18n('Newly attached baggage')}}
                            <span class="ab-badge ab-badge-success">{{ createdBaggages.length }}</span>
                        </div>
                        <button type="button" class="btn base_btn solid_blue ab-print-all"
                                @click="printAll(createdBaggages)">
                            <i class="mdi mdi-printer"></i> {{i18n('Print all')}}
                        </button>
                    </div>
                    <div class="ab-baggage-list">
                        <div v-for="(baggage, index) in createdBaggages" :key="baggage.id" class="ab-baggage-row">
                            <div class="ab-baggage-row-left">
                                <i class="mdi mdi-bag-personal ab-baggage-icon ab-baggage-icon-success"></i>
                                <div class="ab-info-value">#{{ index + 1 }} — ID {{ baggage.id }}</div>
                            </div>
                            <a :href="baggage.view_url" target="_blank" class="btn-icon" :title="i18n('Print out')">
                                <i class="mdi mdi-printer text-primary"></i>
                            </a>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
export default {
    name: "BaggageAttachComponent",
    data() {
        return {
            passenger_id: null,
            passenger: null,
            count: 1,
            searching: false,
            attaching: false,
            createdBaggages: [],
        }
    },
    methods: {
        findPassenger() {
            if (!this.passenger_id) {
                return;
            }
            this.searching = true;
            this.passenger = null;
            this.createdBaggages = [];
            this.$api.get('baggage-shipping.find-passenger', {passenger_id: this.passenger_id})
                .then(res => {
                    this.searching = false;
                    if (res.errors && res.errors.length > 0) {
                        this.notifyError(res.errors[0].message);
                        return;
                    }
                    this.passenger = res.data;
                })
                .catch(err => {
                    this.searching = false;
                    this.notifyError(err.response && err.response.data && err.response.data.errors ? err.response.data.errors[0].message : err.message);
                });
        },
        attachBaggage() {
            if (!this.passenger || !this.count) {
                return;
            }
            this.attaching = true;
            this.$api.post('baggage-shipping.attach', {
                passenger_id: this.passenger.id,
                count: this.count,
            })
                .then(res => {
                    this.attaching = false;
                    if (res.errors && res.errors.length > 0) {
                        this.notifyError(res.errors[0].message);
                        return;
                    }
                    this.createdBaggages = res.data.baggages;
                    if (this.passenger) {
                        this.passenger.existing_baggages = (this.passenger.existing_baggages || []).concat(res.data.baggages);
                    }
                    this.$notify({
                        type: 'success',
                        group: 'foo',
                        title: '',
                        text: this.i18n('Baggage attached'),
                    });
                })
                .catch(err => {
                    this.attaching = false;
                    this.notifyError(err.response && err.response.data && err.response.data.errors ? err.response.data.errors[0].message : err.message);
                });
        },
        printAll(baggages) {
            if (!baggages || baggages.length === 0) {
                return;
            }
            let origin = new URL(baggages[0].view_url).origin;
            let query = baggages.map(function (baggage) {
                return 'uuids[]=' + encodeURIComponent(baggage.uuid);
            }).join('&');
            window.open(origin + '/baggage/print?' + query, '_blank');
        },
        notifyError(message) {
            this.$notify({
                type: 'error',
                group: 'foo',
                title: '',
                text: message,
            });
        },
    }
}
</script>

<style scoped>
.ab-card {
    margin-bottom: 16px;
}

.ab-fade-in {
    animation: ab-fade-in .25s ease;
}

@keyframes ab-fade-in {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
}

.ab-card-label {
    font-size: 13px;
    font-weight: 700;
    color: #7b8794;
    text-transform: uppercase;
    letter-spacing: .03em;
    margin-bottom: 10px;
}

.ab-card-label .mdi {
    margin-right: 4px;
}

.ab-search-row {
    display: flex;
    gap: 10px;
}

.ab-search-row .base_input {
    flex: 1;
}

.ab-search-btn, .ab-attach-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
}

.ab-info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
}

.ab-info-item {
    display: flex;
    align-items: center;
    gap: 12px;
}

.ab-info-icon {
    font-size: 26px;
    color: #0094FF;
    background: #eaf5ff;
    border-radius: 50%;
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
}

.ab-info-label {
    font-size: 12px;
    color: #9aa4b2;
}

.ab-info-value {
    font-size: 15px;
    font-weight: 700;
    color: #202346;
}

.ab-section-hdr {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
    flex-wrap: wrap;
    gap: 10px;
}

.ab-section-ttl {
    font-size: 15px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 8px;
}

.ab-badge {
    background: #eaedf1;
    color: #202346;
    border-radius: 12px;
    padding: 2px 10px;
    font-size: 12px;
    font-weight: 700;
}

.ab-badge-success {
    background: #e3f9ee;
    color: #16a673;
}

.ab-print-all {
    display: flex;
    align-items: center;
    gap: 6px;
}

.ab-baggage-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.ab-baggage-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    background: #f8fafc;
    border-radius: 6px;
    border: 1px solid #eaedf1;
}

.ab-baggage-row-left {
    display: flex;
    align-items: center;
    gap: 12px;
}

.ab-baggage-icon {
    font-size: 20px;
    color: #7b8794;
}

.ab-baggage-icon-success {
    color: #16a673;
}

.ab-attach-row {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

.ab-stepper {
    display: flex;
    align-items: center;
    border: 1px solid #dde0e5;
    border-radius: 6px;
    overflow: hidden;
}

.ab-stepper-btn {
    width: 42px;
    height: 42px;
    border: 0;
    background: #f8fafc;
    color: #202346;
    font-size: 18px;
    cursor: pointer;
}

.ab-stepper-btn:disabled {
    opacity: .4;
    cursor: not-allowed;
}

.ab-stepper-input {
    width: 60px;
    height: 42px;
    border: 0;
    border-left: 1px solid #dde0e5;
    border-right: 1px solid #dde0e5;
    text-align: center;
    font-size: 16px;
    font-weight: 700;
}

.ab-success-card {
    border: 1px solid #c7f0de;
}

@media (max-width: 576px) {
    .ab-search-row {
        flex-direction: column;
    }
}
</style>
